import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Upload, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { CopyButton, fmt } from "@/components/shared";

const QR = (data) => `https://api.qrserver.com/v1/create-qr-code/?size=160x160&bgcolor=0A0F1D&color=00F0FF&data=${encodeURIComponent(data)}`;
const field = "w-full bg-slate-900/60 border border-cyan-500/20 rounded-lg px-4 py-2.5 text-slate-100 focus:border-cyan-400 focus:outline-none";

export function ShareDepositForm({ info, settings, onDone }) {
  const [network, setNetwork] = useState("TRC20");
  const [shares, setShares] = useState("1");
  const [amount, setAmount] = useState(String(info.share_price));
  const [hash, setHash] = useState("");
  const [notes, setNotes] = useState("");
  const [proof, setProof] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const wallet = network === "TRC20" ? settings.trc20_wallet_address : settings.bep20_wallet_address;
  const required = (parseInt(shares, 10) || 0) * info.share_price;

  const onShares = (v) => { setShares(v); const n = parseInt(v, 10); if (n > 0) setAmount(String(n * info.share_price)); };
  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) return toast.error("Only JPG, PNG, WEBP allowed");
    if (file.size > 5 * 1024 * 1024) return toast.error("Max file size is 5MB");
    const reader = new FileReader();
    reader.onload = () => setProof(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    const n = parseInt(shares, 10), amt = parseFloat(amount);
    if (!n || n < 1) return toast.error("Enter the number of shares you intend to buy");
    if (!amt || amt <= 0) return toast.error("Enter the deposit amount");
    if (amt < required) return toast.error(`${n} share(s) require at least ${fmt(required)} USDT`);
    if (!hash.trim()) return toast.error("Transaction ID / hash is required");
    if (!proof) return toast.error("Payment proof is required");
    setLoading(true);
    try {
      const r = await api.post("/shares/deposit", { amount: amt, network, transaction_hash: hash, proof_url: proof, intended_shares: n, notes });
      toast.success("Deposit submitted — Pending Verification");
      onDone?.();
      nav(`/share-confirmation/deposit/${r.data.deposit.id}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="share-deposit-section">
      <div className="glass rounded-2xl p-6 space-y-5">
        <h3 className="font-heading text-lg text-white">Deposit for Shares</h3>
        <p className="text-sm text-slate-400">Send USDT to the platform wallet, then submit your transaction details. Shares are issued only after manual verification.</p>
        <div className="flex gap-3">
          {["TRC20", "BEP20"].map((n) => (
            <button key={n} type="button" data-testid={`share-deposit-network-${n}`} onClick={() => setNetwork(n)}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium border transition-colors ${network === n ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300" : "border-white/10 text-slate-400 hover:border-cyan-500/20"}`}>
              {n === "TRC20" ? "USDT · TRC20 (Tron)" : "USDT · BEP20 (BNB)"}
            </button>
          ))}
        </div>
        <div className="bg-slate-900/60 rounded-xl p-5 border border-cyan-500/10 flex flex-col sm:flex-row gap-5 items-center">
          <img src={QR(wallet)} alt="QR" className="w-36 h-36 rounded-lg border border-cyan-500/20" />
          <div className="flex-1 w-full">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Wallet Address</p>
            <p className="font-mono text-xs text-cyan-300 break-all mb-3" data-testid="share-deposit-wallet">{wallet}</p>
            <CopyButton value={wallet} testId="share-wallet-copy" label="Copy Address" />
          </div>
        </div>
        <p className="text-xs text-amber-200/70">Only send USDT on the selected network. Deposits are reviewed manually and are never converted into shares automatically.</p>
      </div>

      <form onSubmit={submit} className="glass rounded-2xl p-6 space-y-4" data-testid="share-deposit-form">
        <h3 className="font-heading text-lg text-white">Submit Share Deposit</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-slate-300 mb-1.5 block">Intended Number of Shares</label>
            <input data-testid="share-deposit-shares" type="number" min={1} value={shares} onChange={(e) => onShares(e.target.value)} className={field} />
            <p className="text-[11px] text-slate-500 mt-1">= {fmt(required)} USDT at {fmt(info.share_price)} / share</p>
          </div>
          <div>
            <label className="text-sm text-slate-300 mb-1.5 block">Deposit Amount (USDT)</label>
            <input data-testid="share-deposit-amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={field} />
          </div>
        </div>
        <div>
          <label className="text-sm text-slate-300 mb-1.5 block">Transaction ID / Hash</label>
          <input data-testid="share-deposit-hash" value={hash} onChange={(e) => setHash(e.target.value)} className={`${field} font-mono text-sm`} placeholder="0x... / TxID" />
        </div>
        <div>
          <label className="text-sm text-slate-300 mb-1.5 block">Payment Proof</label>
          {proof ? (
            <div className="relative">
              <img src={proof} alt="Preview" className="w-full h-36 object-cover rounded-lg border border-cyan-500/20" />
              <button type="button" onClick={() => setProof("")} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-slate-900/80 flex items-center justify-center text-slate-200"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center h-28 border-2 border-dashed border-cyan-500/20 rounded-lg cursor-pointer hover:border-cyan-500/40 transition-colors">
              <Upload className="w-6 h-6 text-cyan-400 mb-2" />
              <span className="text-xs text-slate-400">Click to upload (JPG, PNG, WEBP · max 5MB)</span>
              <input data-testid="share-deposit-proof" type="file" accept="image/*" className="hidden" onChange={onFile} />
            </label>
          )}
        </div>
        <div>
          <label className="text-sm text-slate-300 mb-1.5 block">Notes (optional)</label>
          <input data-testid="share-deposit-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className={field} placeholder="Any reference" />
        </div>
        <button type="submit" disabled={loading || !info.sales_enabled} data-testid="share-deposit-submit"
          className="btn-glow w-full py-3 rounded-lg font-semibold text-slate-950 inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Submit Deposit for Shares
        </button>
      </form>
    </div>
  );
}
